import React, { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import "./AccessoriesMetals.css";
import { useTranslation } from "react-i18next";
import { getMetalFrames } from "../../../../api/metalFrames";
import { resolveMediaUrl } from "../../../../utils/media";
import { formatUnitSuperscriptText } from "../../../../utils/unitSuperscriptText";

const CEILING_DISPLAY_ORDER = [
  "primary channel",
  "furring channel",
  "perimeter l angle",
  "perimeter angle",
  "primary channel bracket",
  "primary channek brack",
  "threaded rod",
  "threated rod",
  "cd60",
  "cd 60",
  "cd60 channel",
  "cd 60 channel",
  "ud28 channel",
  "ud channel",
  "rapid hnager fix",
  "rapid hanger fix",
  "w angle",
  "shadow angle",
];

const normalizeFrameTitle = (value) =>
  String(value || "")
    .toLowerCase()
    .replace(/[-_]/g, " ")
    .replace(/\s+/g, " ")
    .trim();

const CEILING_ORDER_MAP = CEILING_DISPLAY_ORDER.reduce((acc, title, index) => {
  const key = normalizeFrameTitle(title);
  if (!(key in acc)) acc[key] = index;
  return acc;
}, {});

const CeilingMetalFramesList = () => {
  const { i18n } = useTranslation();
  const [apiItems, setApiItems] = useState([]);

  useEffect(() => {
    let isMounted = true;
    const load = async () => {
      try {
        const data = await getMetalFrames({
          language: String(i18n.language || "en").toLowerCase(),
          frameType: "ceiling-metal-frame",
        });
        if (isMounted) {
          setApiItems(Array.isArray(data) ? data : []);
        }
      } catch (_error) {
        if (isMounted) {
          setApiItems([]);
        }
      }
    };
    load();
    return () => {
      isMounted = false;
    };
  }, [i18n.language]);

  const items = useMemo(() => {
    const mapped = (apiItems || [])
      .map((item) => {
        const key = item?.frameKey || item?.id;
        if (!key) return null;
        return {
          id: String(key),
          title: item?.name || item?.heroTitle || "Ceiling Metal Frame",
          description:
            item?.heroDescription || "Ceiling metal frame details are available in the product page.",
          image: resolveMediaUrl(item?.imageUrl) || "",
        };
      })
      .filter(Boolean);

    return [...mapped].sort((a, b) => {
      const aOrder = CEILING_ORDER_MAP[normalizeFrameTitle(a?.title)];
      const bOrder = CEILING_ORDER_MAP[normalizeFrameTitle(b?.title)];
      const aRank = Number.isFinite(aOrder) ? aOrder : Number.MAX_SAFE_INTEGER;
      const bRank = Number.isFinite(bOrder) ? bOrder : Number.MAX_SAFE_INTEGER;
      if (aRank !== bRank) return aRank - bRank;
      return String(a?.title || "").localeCompare(String(b?.title || ""));
    });
  }, [apiItems]);

  return (
    <section className="accessoriesMetals">
      <div className="accessoriesMetals-grid">
        {items.map((item) => (
          <Link
            key={item.id}
            to={`/products/ceiling-metal-frame/${item.id}`}
            className="accessoriesMetals-card"
          >
            <div className="accessoriesMetals-imageWrap">
              <img src={item.image} alt={item.title} />
            </div>

            <div className="accessoriesMetals-content">
              <h3>{formatUnitSuperscriptText(item.title)}</h3>
              <p>{formatUnitSuperscriptText(item.description)}</p>
            </div>
          </Link>
        ))}
      </div>
    </section>
  );
};

export default CeilingMetalFramesList;
